import React, { useState, useEffect, useCallback, useRef } from 'react';
import Toast from './Toast';
import ReportViewer from './ReportViewer';
import useToast from '../hooks/useToast';

const DAYS      = ['mon','tue','wed','thu','fri','sat','sun'];
const DAY_LABEL = { mon:'Mo',tue:'Di',wed:'Mi',thu:'Do',fri:'Fr',sat:'Sa',sun:'So' };
// Fallback, falls /api/models nicht erreichbar ist — sonst kommt die Liste vom Backend,
// damit neu eingetragene Modelle hier automatisch erscheinen (kein Nachpflegen mehr).
const MODEL_FALLBACK = ['gemma4', 'qwen', 'qwen-fast'];
const ENGINE_OPTS = ['searxng', 'ddg'];

const STATUS_META = {
  pending: { cls:'status-pending', dot:'●', label:'Ausstehend' },
  running: { cls:'status-running', dot:'◎', label:'Läuft' },
  done:    { cls:'status-done',    dot:'●', label:'Fertig' },
  error:   { cls:'status-error',   dot:'●', label:'Fehler' },
};

const fmtDate = iso => iso ? new Date(iso).toLocaleString('de-DE', { dateStyle:'short', timeStyle:'short' }) : '—';

const formatDayList = (days, fallback) => {
  if (Array.isArray(days) && days.length) return days.map(d => DAY_LABEL[d] || d).join('+');
  if (fallback) return DAY_LABEL[fallback] || fallback;
  return '';
};

const scheduleLabel = job => {
  if (job.schedule === 'once')     return 'Einmalig';
  if (job.schedule === 'manual')   return 'Manuell';
  if (job.schedule === 'daily')    return `Täglich ${job.sched_time}`;
  if (job.schedule === 'weekly') {
    const d = formatDayList(job.schedule_days, job.sched_day);
    return d ? `${d} ${job.sched_time}` : `Wöchentlich ${job.sched_time}`;
  }
  if (job.schedule === 'biweekly') {
    const d = formatDayList(job.schedule_days, job.sched_day);
    return `14-tägig${d ? ` ${d}` : ''} ${job.sched_time}`;
  }
  return job.schedule;
};

const EMPTY_FORM = {
  topic:'', model:'gemma4', schedule:'daily', sched_time:'07:00',
  schedule_days: [], sched_day: 'mon',
  // Erweitert (Phase 10)
  system_prompt_override: '',
  query_rotation_text: '',  // im UI als mehrzeilige Eingabe; Serialisierung in submit
  engines: [],
  whitelist_text: '',
  blacklist_text: '',
  timerange_days: '',
  max_iterations: '',
};

export default function JobsView({ authToken, onLogout, onDiscussReport, initialTopic, onInitialTopicConsumed, onHelp }) {
  const [jobs,          setJobs]          = useState([]);
  const [reports,       setReports]       = useState([]);
  const [selectedJob,   setSelectedJob]   = useState(null);
  const [viewReport,    setViewReport]    = useState(null);
  const [showForm,      setShowForm]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy,          setBusy]          = useState('');
  const [toasts,        pushToast]        = useToast();
  // Phase 10
  const [editingJobId,  setEditingJobId]  = useState(null);
  const [advancedOpen,  setAdvancedOpen]  = useState(false);
  // Ad-hoc-Recherchen (once-Jobs aus Loop-Closure) standardmäßig ausblenden
  const [showAdhoc, setShowAdhoc] = useState(false);
  // 🧹 Aufräumen: Compare-Prozess schlägt Archiv-Kandidaten vor (kein Auto-Delete)
  const [cleaningMode, setCleaningMode] = useState(false);
  const [cleaning,     setCleaning]     = useState(null);    // {candidates, archived_total}
  const [cleaningSel,  setCleaningSel]  = useState([]);      // gewählte Report-IDs
  const [archivedList, setArchivedList] = useState(null);    // null = zu; [] = Ansicht offen
  // Recherchen kombinieren (Migration 010)
  const [combineMode,    setCombineMode]    = useState(false);
  const [combineSel,     setCombineSel]     = useState([]);   // gewählte Job-IDs
  const [combineBusy,    setCombineBusy]    = useState(false);
  const [combineHistory, setCombineHistory] = useState([]);   // Verlauf (#3)
  const [combineSuggest, setCombineSuggest] = useState([]);   // Auto-Vorschläge (#2)

  const [modelOpts, setModelOpts] = useState(MODEL_FALLBACK.map(m => ({ key: m, name: m })));

  const [form, setForm] = useState(EMPTY_FORM);

  const auth = { Authorization: `Bearer ${authToken}` };

  const loadJobs = useCallback(() => {
    fetch('/api/jobs', { headers: auth })
      .then(r => {
        if (r.status === 401) {
          pushToast('Sitzung abgelaufen — bitte neu anmelden', 'error');
          onLogout?.();
          return null;
        }
        return r.json();
      })
      .then(d => { if (d) setJobs(d.jobs || []); })
      .catch(() => pushToast('Jobs konnten nicht geladen werden', 'error'));
  
  }, [authToken, onLogout]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Modell-Liste aus dem Backend ziehen (Single Source of Truth: config_models.py)
  useEffect(() => {
    fetch('/api/models', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const ms = d && d.models;
        if (ms && Object.keys(ms).length) {
          setModelOpts(Object.entries(ms).map(([key, v]) => ({ key, name: v.name || key })));
        }
      })
      .catch(() => {});
  }, [authToken]);

  // Initial-Topic von außen (z.B. /schedule aus Chat)
  useEffect(() => {
    if (initialTopic) {
      setForm(f => ({ ...f, topic: initialTopic }));
      setShowForm(true);
      onInitialTopicConsumed?.();
    }
  }, [initialTopic, onInitialTopicConsumed]);

  useEffect(() => {
    const hasRunning = jobs.some(j => j.status === 'running');
    if (!hasRunning) return;
    const id = setInterval(loadJobs, 5000);
    return () => clearInterval(id);
  }, [jobs, loadJobs]);

  const loadReports = (jobId) => {
    setSelectedJob(jobId);
    fetch(`/api/jobs/${jobId}/reports`, { headers: auth })
      .then(r => r.json())
      .then(d => setReports(d.reports || []))
      .catch(() => pushToast('Reports konnten nicht geladen werden', 'error'));
  };

  // Phase 10: Form-State → Backend-Body (bereinigt leere optionale Felder)
  const buildBody = () => {
    const splitLines = s => (s || '').split('\n').map(x => x.trim()).filter(Boolean);
    const body = {
      topic:      form.topic,
      model:      form.model,
      schedule:   form.schedule,
      sched_time: form.sched_time,
    };
    // schedule_days nur wenn schedule weekly/biweekly + Tage gewählt
    if ((form.schedule === 'weekly' || form.schedule === 'biweekly') && form.schedule_days.length) {
      body.schedule_days = form.schedule_days;
    }
    // sched_day legacy fallback wenn weekly + keine schedule_days gesetzt
    if (form.schedule === 'weekly' && !form.schedule_days.length && form.sched_day) {
      body.sched_day = form.sched_day;
    }
    // Erweiterte Felder — nur senden wenn nicht leer
    if (form.system_prompt_override?.trim()) body.system_prompt_override = form.system_prompt_override.trim();
    const queries = splitLines(form.query_rotation_text);
    if (queries.length) body.query_rotation = queries;
    if (form.engines.length) body.engines = form.engines;
    const wl = splitLines(form.whitelist_text);
    if (wl.length) body.whitelist_domains = wl;
    const bl = splitLines(form.blacklist_text);
    if (bl.length) body.blacklist_domains = bl;
    if (form.timerange_days) body.timerange_days = parseInt(form.timerange_days, 10);
    if (form.max_iterations) body.max_iterations = parseInt(form.max_iterations, 10);
    return body;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy('create');
    try {
      const isEdit = editingJobId !== null;
      const url    = isEdit ? `/api/jobs/${editingJobId}` : '/api/jobs';
      const method = isEdit ? 'PATCH' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      });
      const d = await r.json();
      if (!r.ok) {
        // Pydantic-422 zeigt details[] mit field+msg — User-friendlich aufbereiten
        const detail = (d.details || []).map(x => `${x.field}: ${x.msg}`).join(' · ');
        pushToast(d.error ? `${d.error}${detail ? ' — ' + detail : ''}` : 'Fehler beim Speichern', 'error');
        return;
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingJobId(null);
      setAdvancedOpen(false);
      loadJobs();
      pushToast(isEdit ? 'Job aktualisiert' : 'Job erstellt', 'success');
    } catch { pushToast('Netzwerkfehler', 'error'); }
    finally  { setBusy(''); }
  };

  const handleEdit = (job) => {
    setForm({
      topic:                  job.topic || '',
      model:                  job.model || 'gemma4',
      schedule:               job.schedule || 'daily',
      sched_time:             job.sched_time || '07:00',
      schedule_days:          Array.isArray(job.schedule_days) ? job.schedule_days : [],
      sched_day:              job.sched_day || 'mon',
      system_prompt_override: job.system_prompt_override || '',
      query_rotation_text:    Array.isArray(job.query_rotation) ? job.query_rotation.join('\n') : '',
      engines:                Array.isArray(job.engines) ? job.engines : [],
      whitelist_text:         Array.isArray(job.whitelist_domains) ? job.whitelist_domains.join('\n') : '',
      blacklist_text:         Array.isArray(job.blacklist_domains) ? job.blacklist_domains.join('\n') : '',
      timerange_days:         job.timerange_days || '',
      max_iterations:         job.max_iterations || '',
    });
    setEditingJobId(job.id);
    setShowForm(true);
    // Erweitert-Akkordion öffnen wenn der Job erweiterte Felder hat
    setAdvancedOpen(!!(
      job.system_prompt_override || job.query_rotation || job.engines ||
      job.whitelist_domains || job.blacklist_domains ||
      job.timerange_days || job.max_iterations
    ));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingJobId(null);
    setAdvancedOpen(false);
    setForm(EMPTY_FORM);
  };

  // Wochentag-Toggle für Multi-Select
  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      schedule_days: f.schedule_days.includes(day)
        ? f.schedule_days.filter(d => d !== day)
        : [...f.schedule_days, day],
    }));
  };

  const toggleEngine = (eng) => {
    setForm(f => ({
      ...f,
      engines: f.engines.includes(eng)
        ? f.engines.filter(e => e !== eng)
        : [...f.engines, eng],
    }));
  };

  const handleDelete = async (jobId) => {
    setBusy(`del-${jobId}`);
    await fetch(`/api/jobs/${jobId}`, { method:'DELETE', headers:auth }).catch(()=>{});
    if (selectedJob === jobId) { setSelectedJob(null); setReports([]); }
    setConfirmDelete(null);
    loadJobs();
    setBusy('');
    pushToast('Job gelöscht', 'info');
  };

  const handleToggle = async (jobId) => {
    setBusy(`tog-${jobId}`);
    const r = await fetch(`/api/jobs/${jobId}/toggle`, { method:'POST', headers:auth }).catch(()=>null);
    if (r?.ok) {
      const d = await r.json();
      pushToast(d.enabled ? 'Job aktiviert' : 'Job pausiert', 'info');
    }
    loadJobs();
    setBusy('');
  };

  const handleRunNow = async (jobId) => {
    setBusy(`run-${jobId}`);
    const r = await fetch(`/api/jobs/${jobId}/run`, { method:'POST', headers:auth }).catch(()=>null);
    if (r?.ok) {
      pushToast('Job gestartet — läuft im Hintergrund', 'success');
      setTimeout(loadJobs, 2000);
      setTimeout(loadJobs, 8000);
      setTimeout(loadJobs, 20000);
    } else {
      pushToast('Job konnte nicht gestartet werden', 'error');
    }
    setBusy('');
  };

  // ===== 🧹 Aufräumen (Archiv statt Löschen) =====
  const loadCleaning = useCallback(() => {
    setCleaning(null);
    fetch('/api/reports/cleaning', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setCleaning(d);
        setCleaningSel((d.candidates || []).map(c => c.id));   // alle vorausgewählt
      })
      .catch(() => { setCleaning({ candidates: [], archived_total: 0 }); pushToast('Kandidaten nicht ladbar', 'error'); });
  }, [authToken]);

  const enterCleaning = () => {
    setCombineMode(false); setCombineSel([]);
    setCleaningMode(true); setArchivedList(null);
    setSelectedJob(null); setReports([]);
    loadCleaning();
  };
  const exitCleaning = () => { setCleaningMode(false); setCleaning(null); setCleaningSel([]); setArchivedList(null); };

  const toggleCleaningSel = (id) => {
    setCleaningSel(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };

  const archiveSelected = async () => {
    if (!cleaningSel.length) return;
    setBusy('archive');
    const r = await fetch('/api/reports/archive', {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: cleaningSel, archived: true }),
    }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r?.ok) {
      pushToast(`🧹 ${d.changed} Report${d.changed === 1 ? '' : 's'} archiviert — raus aus Suche & Gedächtnis, jederzeit wiederherstellbar`, 'success');
      loadCleaning();
    } else {
      pushToast(d.error || 'Archivieren fehlgeschlagen', 'error');
    }
    setBusy('');
  };

  const loadArchived = async () => {
    const r = await fetch('/api/reports/archived', { headers: auth }).catch(() => null);
    const d = r && r.ok ? await r.json().catch(() => null) : null;
    if (d) setArchivedList(d.items || []);
    else pushToast('Archiv nicht ladbar', 'error');
  };

  const restoreReport = async (id) => {
    setBusy(`restore-${id}`);
    const r = await fetch('/api/reports/archive', {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [id], archived: false }),
    }).catch(() => null);
    if (r?.ok) { pushToast('Report wiederhergestellt', 'success'); loadArchived(); loadCleaning(); }
    else pushToast('Wiederherstellen fehlgeschlagen', 'error');
    setBusy('');
  };

  // ===== Recherchen kombinieren =====
  // Poll-Steuerung mit Cancel-Token: verhindert Timer-Leak (Modus verlassen/Unmount)
  // und parallele Poll-Ketten (Re-Run während ein Lauf noch pollt).
  const pollRef = useRef({ timer: null, token: 0 });
  const stopPoll = () => {
    pollRef.current.token++;
    if (pollRef.current.timer) { clearTimeout(pollRef.current.timer); pollRef.current.timer = null; }
  };
  useEffect(() => () => stopPoll(), []);   // Cleanup beim Unmount

  const exitCombine = () => { stopPoll(); setCombineMode(false); setCombineSel([]); };

  const loadCombineHistory = useCallback(() => {
    fetch('/api/combine', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json()).then(d => setCombineHistory(d.runs || []))
      .catch(() => {});
  }, [authToken]);

  const loadCombineSuggest = useCallback(() => {
    fetch('/api/combine/suggestions', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json()).then(d => setCombineSuggest(d.suggestions || []))
      .catch(() => {});
  }, [authToken]);

  const toggleCombine = (jobId) => {
    setCombineSel(sel =>
      sel.includes(jobId) ? sel.filter(x => x !== jobId)
      : sel.length >= 4   ? sel                    // max 4 Themen
      : [...sel, jobId]);
  };

  const pollCombine = (runId) => {
    stopPoll();                                  // evtl. laufende Kette beenden
    const myToken = pollRef.current.token;
    let tries = 0;
    const tick = async () => {
      if (myToken !== pollRef.current.token) return;   // abgebrochen/überholt
      tries++;
      const r = await fetch(`/api/combine/${runId}`, { headers: auth }).catch(() => null);
      if (myToken !== pollRef.current.token) return;
      const d = r && r.ok ? await r.json() : null;
      if (d && (d.status === 'done' || d.status === 'error')) {
        if (d.status === 'done') {
          pushToast(`✓ ${d.idea_count} Themenkinder erstellt — im Ideen-Tab unter „Offen"`, 'success');
          setCombineSel([]);
        } else {
          pushToast(`Kombination fehlgeschlagen: ${(d.error || '').slice(0, 120)}`, 'error');
        }
        setCombineBusy(false);
        loadCombineHistory();
        return;
      }
      if (tries < 90) pollRef.current.timer = setTimeout(tick, 5000);  // bis ~7.5 min
      else { setCombineBusy(false); pushToast('Kombination dauert länger als erwartet — prüfe den Ideen-Tab', 'info'); loadCombineHistory(); }
    };
    pollRef.current.timer = setTimeout(tick, 4000);
  };

  const runCombine = async (jobIds) => {
    const ids = jobIds || combineSel;
    if (ids.length < 2) return;
    setCombineBusy(true);
    try {
      const r = await fetch('/api/combine', {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_ids: ids }),
      });
      const d = await r.json();
      if (!r.ok) {
        const detail = (d.details || []).map(x => `${x.field}: ${x.msg}`).join(' · ');
        pushToast(d.error ? `${d.error}${detail ? ' — ' + detail : ''}` : 'Kombination nicht möglich', 'error');
        setCombineBusy(false);
        return;
      }
      if (!d.run_id) {   // Server-Vertragsbruch — nicht ins Leere pollen
        pushToast('Unerwartete Server-Antwort (keine run_id)', 'error');
        setCombineBusy(false);
        return;
      }
      pushToast('Kombination läuft (1–3 min) — die Themenkinder erscheinen im Ideen-Tab', 'info');
      loadCombineHistory();
      pollCombine(d.run_id);
    } catch {
      pushToast('Netzwerkfehler', 'error');
      setCombineBusy(false);
    }
  };

  useEffect(() => {
    if (combineMode) { loadCombineHistory(); loadCombineSuggest(); }
  }, [combineMode, loadCombineHistory, loadCombineSuggest]);

  // Ad-hoc-Recherchen (einmalige Loop-Closure-Jobs) trennen — sie würden die
  // Liste der gepflegten Themen-Jobs zumüllen
  const adhocJobs   = jobs.filter(j => j.schedule === 'once');
  const visibleJobs = showAdhoc ? jobs : jobs.filter(j => j.schedule !== 'once');
  const activeJobs  = jobs.filter(j => j.enabled).length;
  const runningJobs = jobs.filter(j => j.status === 'running').length;
  const totalReportCount = jobs.reduce((s,j) => s + (j.report_count||0), 0);

  return (
    <div className="jobs-view">
      <Toast messages={toasts} />

      {viewReport !== null && (
        <ReportViewer
          reportId={viewReport}
          authToken={authToken}
          onClose={() => setViewReport(null)}
          onDiscussWithAI={onDiscussReport ? (report) => {
            onDiscussReport(report);
            setViewReport(null);
          } : undefined}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Recherche-Jobs</h1>
          <p className="page-sub">Autonome KI-Recherche zu Themen deiner Wahl</p>
        </div>
        <div className="page-header-actions">
          {onHelp && (
            <button className="btn-ghost-sm help-btn"
              onClick={() => onHelp(combineMode ? 'kombinieren' : cleaningMode ? 'cleaning' : 'jobs')}
              title="Hilfe zu dieser Ansicht öffnen">❓</button>
          )}
          <button className="btn-outline"
            onClick={() => cleaningMode ? exitCleaning() : enterCleaning()}
            disabled={combineMode}
            title={cleaningMode ? 'Aufräum-Modus verlassen'
              : 'Archiv-Kandidaten prüfen: quellenlose, winzige und doppelte Reports (archivieren, nicht löschen)'}>
            {cleaningMode ? '✕ Aufräumen' : '🧹 Aufräumen'}
          </button>
          <button className="btn-outline"
            onClick={() => { if (combineMode) { exitCombine(); } else { exitCleaning(); setCombineMode(true); setShowForm(false); } }}
            disabled={jobs.length < 2}
            title={jobs.length < 2 ? 'Mindestens 2 Themen nötig zum Kombinieren'
              : combineMode ? 'Kombinieren-Modus verlassen'
              : 'Zwei bestehende Recherche-Themen zu neuen offenen Ideen kombinieren'}>
            {combineMode ? '✕ Kombinieren' : '⊕ Kombinieren'}
          </button>
          <button className="btn-primary" onClick={() => showForm ? handleCancel() : setShowForm(true)}
            disabled={combineMode}
            title={showForm ? 'Formular schließen' : 'Neuen Recherche-Job anlegen'}>
            {showForm ? '✕ Abbrechen' : '+ Neuer Job'}
          </button>
        </div>
      </div>

      {jobs.length > 0 && (
        <div className="stats-row">
          <div className="stat-card"><span className="stat-value">{jobs.length}</span><span className="stat-label">Jobs gesamt</span></div>
          <div className="stat-card"><span className="stat-value">{activeJobs}</span><span className="stat-label">Aktiv</span></div>
          {runningJobs > 0 && (
            <div className="stat-card stat-card-accent"><span className="stat-value running-pulse">{runningJobs}</span><span className="stat-label">Läuft gerade</span></div>
          )}
          <div className="stat-card"><span className="stat-value">{totalReportCount}</span><span className="stat-label">Reports</span></div>
        </div>
      )}

      {showForm && (
        <div className="form-card">
          <h2 className="form-title">{editingJobId ? 'Job bearbeiten' : 'Neuen Recherche-Job anlegen'}</h2>
          <form onSubmit={handleSubmit} className="job-form">
            <div className="form-group">
              <label className="field-label">Recherche-Thema</label>
              <input
                className="field-input field-input-lg"
                type="text"
                placeholder="z.B. Aktuelle Entwicklungen in der KI-Regulierung"
                value={form.topic}
                onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                required maxLength={500}
                autoFocus
              />
            </div>

            <div className="form-row-2">
              <div className="form-group">
                <label className="field-label">Modell</label>
                <select className="field-select" value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))}>
                  {modelOpts.map(m => <option key={m.key} value={m.key}>{m.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="field-label">Frequenz</label>
                <select className="field-select" value={form.schedule} onChange={e => setForm(f => ({ ...f, schedule: e.target.value }))}>
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="biweekly">14-tägig</option>
                  <option value="manual">Manuell (kein Schedule)</option>
                  <option value="once">Einmalig jetzt</option>
                </select>
              </div>
              {form.schedule !== 'once' && form.schedule !== 'manual' && (
                <div className="form-group">
                  <label className="field-label">Uhrzeit</label>
                  <input className="field-input" type="time" value={form.sched_time}
                    onChange={e => setForm(f => ({ ...f, sched_time: e.target.value }))} />
                </div>
              )}
            </div>

            {/* Phase 10: Multi-Day-Selector für weekly/biweekly */}
            {(form.schedule === 'weekly' || form.schedule === 'biweekly') && (
              <div className="form-group">
                <label className="field-label">
                  {form.schedule === 'biweekly' ? 'Anker-Wochentag (alle 14 Tage)' : 'Wochentage (mehrfach möglich)'}
                </label>
                <div className="weekday-toggle-row">
                  {DAYS.map(d => (
                    <button
                      key={d} type="button"
                      className={`weekday-toggle ${form.schedule_days.includes(d) ? 'weekday-toggle-on' : ''}`}
                      onClick={() => toggleDay(d)}
                      title={`Wochentag ${DAY_LABEL[d]} ${form.schedule_days.includes(d) ? 'abwählen' : 'wählen'}`}
                    >{DAY_LABEL[d]}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Phase 10: Erweitert-Akkordion */}
            <div className="form-group">
              <button type="button" className="btn-ghost-sm" onClick={() => setAdvancedOpen(o => !o)}
                title={advancedOpen ? 'Erweiterte Optionen ausblenden' : 'System-Prompt, Quellen-Filter & Engines konfigurieren'}>
                {advancedOpen ? '▾' : '▸'} Erweitert (System-Prompt, Quellen-Filter, Engines)
              </button>
            </div>
            {advancedOpen && (
              <div className="form-advanced">
                <div className="form-group">
                  <label className="field-label">System-Prompt-Override (für Triage-Logik wie KERN/KONTEXT/LÄRM)</label>
                  <textarea
                    className="field-input"
                    rows={6}
                    placeholder="Lass leer für Default. Platzhalter: {topic}, {today}"
                    value={form.system_prompt_override}
                    onChange={e => setForm(f => ({ ...f, system_prompt_override: e.target.value }))}
                    maxLength={4000}
                  />
                </div>
                <div className="form-group">
                  <label className="field-label">Query-Rotation (eine Query pro Zeile, max 10) — pro Lauf wird die nächste genommen</label>
                  <textarea
                    className="field-input"
                    rows={4}
                    placeholder="z.B.&#10;KI-Regulierung EU&#10;AI Act Auswirkungen Industrie&#10;DSGVO und Foundation Models"
                    value={form.query_rotation_text}
                    onChange={e => setForm(f => ({ ...f, query_rotation_text: e.target.value }))}
                  />
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="field-label">Such-Engines</label>
                    <div className="engine-toggle-row">
                      {ENGINE_OPTS.map(eng => (
                        <button
                          key={eng} type="button"
                          className={`engine-toggle ${form.engines.includes(eng) ? 'engine-toggle-on' : ''}`}
                          onClick={() => toggleEngine(eng)}
                          title={`Such-Engine ${eng} ${form.engines.includes(eng) ? 'abwählen' : 'wählen'}`}
                        >{eng}</button>
                      ))}
                    </div>
                    <span className="field-hint">Leer = beide (mit Fallback)</span>
                  </div>
                  <div className="form-group">
                    <label className="field-label">Zeitraum (Tage)</label>
                    <input className="field-input" type="number" min="1" max="365"
                      placeholder="z.B. 30 (letzte 30 Tage)"
                      value={form.timerange_days}
                      onChange={e => setForm(f => ({ ...f, timerange_days: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="field-label">Max Iterationen</label>
                    <input className="field-input" type="number" min="1" max="15"
                      placeholder="Default 5"
                      value={form.max_iterations}
                      onChange={e => setForm(f => ({ ...f, max_iterations: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="field-label">Whitelist-Domains (eine pro Zeile)</label>
                    <textarea
                      className="field-input" rows={3}
                      placeholder="heise.de&#10;golem.de"
                      value={form.whitelist_text}
                      onChange={e => setForm(f => ({ ...f, whitelist_text: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="field-label">Blacklist-Domains (eine pro Zeile)</label>
                    <textarea
                      className="field-input" rows={3}
                      placeholder="example-spam.com"
                      value={form.blacklist_text}
                      onChange={e => setForm(f => ({ ...f, blacklist_text: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-outline" onClick={handleCancel}
                title="Formular schließen ohne zu speichern">Abbrechen</button>
              <button type="submit" className="btn-primary" disabled={busy === 'create' || !form.topic.trim()}
                title={editingJobId ? 'Änderungen an diesem Job speichern' : 'Neuen Recherche-Job anlegen'}>
                {busy === 'create' ? (editingJobId ? 'Speichere…' : 'Erstelle…') : (editingJobId ? 'Änderungen speichern' : 'Job erstellen')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="jobs-body">
        <div className="jobs-col">
          {jobs.length === 0 && !showForm && (
            <div className="empty-state-card">
              <div className="empty-icon">⊕</div>
              <p className="empty-title">Noch keine Jobs</p>
              <p className="empty-sub">Erstelle einen Recherche-Job und lass die KI automatisch Berichte sammeln.</p>
              <button className="btn-primary" onClick={() => setShowForm(true)}
                title="Ersten Recherche-Job anlegen">+ Ersten Job erstellen</button>
            </div>
          )}

          {combineMode && (
            <div className="combine-bar">
              <div className="combine-bar-text">
                <strong>Themen kombinieren</strong> — wähle 2–4 Themen. Aus ihren neuesten Reports
                entstehen neue <em>offene</em> Themenkinder im Ideen-Tab (Lastschutz: ein Lauf zur Zeit).
                {combineSel.length > 0 && <span className="combine-bar-count"> · {combineSel.length} gewählt</span>}
              </div>
              <button className="btn-primary" disabled={combineSel.length < 2 || combineBusy}
                onClick={() => runCombine()}
                title={combineSel.length < 2 ? 'Mindestens 2 Themen wählen' : 'Aus den gewählten Themen Themenkinder erzeugen'}>
                {combineBusy ? 'Kombiniere…' : `Kombinieren (${combineSel.length})`}
              </button>
            </div>
          )}

          {combineMode && combineSuggest.length > 0 && (
            <div className="combine-suggest">
              <div className="combine-suggest-label">💡 Vorschläge — überschneiden sich thematisch, noch nie kombiniert:</div>
              <div className="combine-suggest-list">
                {combineSuggest.map((s, i) => (
                  <button key={i} className="combine-suggest-card" disabled={combineBusy}
                    onClick={() => setCombineSel(s.job_ids)}
                    title={`Auswählen: ${s.topics.join(' × ')} — gemeinsam: ${s.shared.join(', ')}`}>
                    <span className="combine-suggest-topics">{s.topics.map(t => (t || '').split(/[:—]/)[0].trim()).join(' × ')}</span>
                    <span className="combine-suggest-shared">{s.shared.slice(0, 4).join(' · ')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {adhocJobs.length > 0 && (
            <button className="btn-ghost-sm adhoc-toggle" onClick={() => setShowAdhoc(s => !s)}
              title={showAdhoc ? 'Einmalige Lücken-Recherchen wieder ausblenden' : 'Einmalige Lücken-Recherchen (Loop-Closure) einblenden'}>
              {showAdhoc ? '▾' : '▸'} {adhocJobs.length} Ad-hoc-Recherche{adhocJobs.length > 1 ? 'n' : ''} {showAdhoc ? 'verbergen' : 'anzeigen'}
            </button>
          )}

          {visibleJobs.map(job => {
            const sm = STATUS_META[job.status] || STATUS_META.pending;
            const isConfirming = confirmDelete === job.id;
            const isSelected   = selectedJob === job.id;
            const canCombine   = (job.report_count || 0) > 0;
            const isCombineSel  = combineSel.includes(job.id);
            return (
              <div key={job.id} className={`job-card ${isSelected?'job-card-selected':''} ${job.status==='running'?'job-card-running':''} ${combineMode?'job-card-combine':''} ${isCombineSel?'job-card-combine-on':''} ${combineMode&&!canCombine?'job-card-combine-disabled':''}`}>
                <div className="job-card-main"
                  onClick={() => combineMode ? (canCombine && toggleCombine(job.id)) : loadReports(job.id)}
                  title={combineMode ? (canCombine ? (isCombineSel ? 'Abwählen' : 'Für Kombination wählen') : 'Keine Reports — nicht kombinierbar') : 'Reports dieses Jobs anzeigen'}>
                  <div className="job-card-top-row">
                    {combineMode && <span className="combine-check" aria-hidden="true">{isCombineSel ? '☑' : (canCombine ? '☐' : '⊘')}</span>}
                    <span
                      className={`status-dot ${sm.cls}`}
                      title={job.status === 'error' && job.error_message ? `Fehler: ${job.error_message}` : sm.label}
                    >{sm.dot}</span>
                    <span className="job-topic">{job.topic}</span>
                  </div>
                  <div className="job-card-meta-row">
                    <span className="meta-chip">{job.model}</span>
                    <span className="meta-chip">{scheduleLabel(job)}</span>
                    {job.report_count > 0 && (
                      <span className="meta-chip meta-chip-accent">{job.report_count} Report{job.report_count>1?'s':''}</span>
                    )}
                    {!job.enabled && <span className="meta-chip meta-chip-muted">pausiert</span>}
                    {job.error_count >= 3 && job.enabled && (
                      <span className="meta-chip meta-chip-warn" title={job.error_message || ''}>
                        {job.error_count} Fehler in Folge
                      </span>
                    )}
                  </div>
                  {/* Phase 10: Auto-Pause-Banner */}
                  {!job.enabled && (job.error_count || 0) >= 5 && (
                    <div className="job-error-banner" title={job.error_message || ''}>
                      ⛔ Auto-deaktiviert nach 5 Fehlern. Letzter: {(job.error_message || '').slice(0, 100)}
                    </div>
                  )}
                  {job.last_run && <div className="job-last-run">Letzter Lauf: {fmtDate(job.last_run)}</div>}
                </div>

                {!combineMode && (
                <div className="job-card-actions">
                  {isConfirming ? (
                    <div className="inline-confirm">
                      <span>Löschen?</span>
                      <button className="btn-danger-sm" onClick={() => handleDelete(job.id)} disabled={!!busy} title="Job endgültig löschen">Ja</button>
                      <button className="btn-ghost-sm"  onClick={() => setConfirmDelete(null)} title="Löschen abbrechen">Nein</button>
                    </div>
                  ) : (
                    <>
                      <button className="icon-action-btn icon-action-run" onClick={() => handleRunNow(job.id)}
                        disabled={!!busy || job.status==='running'} title="Jetzt ausführen">
                        {busy===`run-${job.id}` ? '…' : '▶'}
                      </button>
                      <button className="icon-action-btn icon-action-edit"
                        onClick={() => handleEdit(job)} disabled={!!busy} title="Bearbeiten">✎</button>
                      <button className={`icon-action-btn ${job.enabled?'icon-action-pause':'icon-action-play'}`}
                        onClick={() => handleToggle(job.id)} disabled={!!busy} title={job.enabled ? 'Pausieren' : 'Aktivieren'}>
                        {busy===`tog-${job.id}` ? '…' : job.enabled ? '⏸' : '▷'}
                      </button>
                      <button className="icon-action-btn icon-action-del" onClick={() => setConfirmDelete(job.id)} title="Löschen">✕</button>
                    </>
                  )}
                </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="reports-col">
          {cleaningMode ? (
            <>
              <div className="reports-col-header">
                <span className="reports-col-title">🧹 Archiv-Kandidaten</span>
                <button className="btn-ghost-sm" onClick={loadCleaning} title="Kandidaten neu berechnen">↻</button>
              </div>
              {cleaning === null && <div className="reports-empty"><p>Vergleiche Reports (Quellen, Größe, Ähnlichkeit)…</p></div>}
              {cleaning && archivedList === null && (
                <>
                  <p className="cleaning-hint">
                    Vorschläge des Compare-Prozesses — <strong>archiviert, nicht gelöscht</strong>: raus aus
                    Suche, Gedächtnis &amp; Drift, jederzeit wiederherstellbar. Ideen-Quellen und der
                    neueste Report je Thema werden nie vorgeschlagen.
                  </p>
                  {cleaning.candidates.length === 0 ? (
                    <div className="reports-empty"><p>✨ Nichts aufzuräumen — der Korpus ist sauber.</p></div>
                  ) : (
                    <>
                      <div className="cleaning-actions">
                        <button className="btn-primary" disabled={!cleaningSel.length || busy === 'archive'}
                          onClick={archiveSelected}
                          title="Ausgewählte Kandidaten archivieren (reversibel)">
                          {busy === 'archive' ? '…' : `🧹 ${cleaningSel.length} archivieren`}
                        </button>
                        <button className="btn-ghost-sm"
                          onClick={() => setCleaningSel(cleaningSel.length === cleaning.candidates.length ? [] : cleaning.candidates.map(c => c.id))}
                          title="Alle an-/abwählen">
                          {cleaningSel.length === cleaning.candidates.length ? 'keine' : 'alle'}
                        </button>
                      </div>
                      <div className="cleaning-list">
                        {cleaning.candidates.map(c => (
                          <label key={c.id} className="cleaning-item" title={c.reasons.join(' · ')}>
                            <input type="checkbox" checked={cleaningSel.includes(c.id)}
                              onChange={() => toggleCleaningSel(c.id)} />
                            <span className="cleaning-item-body">
                              <span className="cleaning-item-top">
                                <button className="dist-title" onClick={e => { e.preventDefault(); setViewReport(c.id); }}
                                  title="Report ansehen">#{c.id} {c.topic?.slice(0, 40)}</button>
                                <span className="dist-date">{c.created_at?.slice(0, 10)}</span>
                              </span>
                              <span className="cleaning-reasons">
                                {c.reasons.map((r, i) => <span key={i} className="cleaning-reason">{r}</span>)}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                  <button className="btn-ghost-sm cleaning-archived-link" onClick={loadArchived}
                    title="Bereits archivierte Reports ansehen und wiederherstellen">
                    📦 Archiv ansehen ({cleaning.archived_total})
                  </button>
                </>
              )}
              {Array.isArray(archivedList) && (
                <>
                  <button className="btn-ghost-sm" onClick={() => setArchivedList(null)} title="Zurück zu den Kandidaten">← Kandidaten</button>
                  {archivedList.length === 0 ? (
                    <div className="reports-empty"><p>Archiv ist leer.</p></div>
                  ) : (
                    <div className="cleaning-list">
                      {archivedList.map(a => (
                        <div key={a.id} className="cleaning-item">
                          <span className="cleaning-item-body">
                            <span className="cleaning-item-top">
                              <button className="dist-title" onClick={() => setViewReport(a.id)} title="Report ansehen">#{a.id} {a.topic?.slice(0, 40)}</button>
                              <span className="dist-date">archiviert {a.archived_at?.slice(0, 10)}</span>
                            </span>
                          </span>
                          <button className="btn-ghost-sm" disabled={!!busy}
                            onClick={() => restoreReport(a.id)} title="Zurück in Suche & Gedächtnis holen">
                            {busy === `restore-${a.id}` ? '…' : '↩ wiederherstellen'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          ) : combineMode ? (
            <>
              <div className="reports-col-header">
                <span className="reports-col-title">Verlauf</span>
                <button className="btn-ghost-sm" onClick={loadCombineHistory} title="Verlauf neu laden">↻</button>
              </div>
              {combineHistory.length === 0 ? (
                <div className="reports-empty">
                  <p>Noch keine Kombinationen.</p>
                  <p className="reports-empty-sub">Wähle 2–4 Themen und klick „Kombinieren".</p>
                </div>
              ) : (
                <div className="combine-history">
                  {combineHistory.map(run => {
                    const short = (run.job_topics || []).map(t => (t || '').split(/[:—]/)[0].trim()).join(' × ');
                    return (
                      <div key={run.id} className={`combine-hist-item combine-hist-${run.status}`}>
                        <div className="combine-hist-top">
                          <span className="combine-hist-status">
                            {run.status === 'done' ? `✓ ${run.idea_count} Idee${run.idea_count===1?'':'n'}`
                              : run.status === 'error' ? '✗ Fehler' : '◴ läuft…'}
                          </span>
                          {run.status !== 'running' && (
                            <button className="btn-ghost-sm" disabled={combineBusy}
                              onClick={() => runCombine(run.job_ids)} title="Diese Kombination erneut starten">↻ Erneut</button>
                          )}
                        </div>
                        <div className="combine-hist-topics" title={(run.job_topics || []).join('  ×  ')}>{short}</div>
                        {run.status === 'error' && run.error && <div className="combine-hist-errmsg">{run.error.slice(0, 120)}</div>}
                        <div className="combine-hist-date">{fmtDate(run.created_at)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : selectedJob === null ? (
            <div className="reports-placeholder">
              <span className="reports-placeholder-icon">←</span>
              <p>Job auswählen um Reports zu sehen</p>
            </div>
          ) : (
            <>
              <div className="reports-col-header">
                <span className="reports-col-title">Reports</span>
                <button className="btn-ghost-sm" onClick={() => { setSelectedJob(null); setReports([]); }} title="Reports-Ansicht schließen">✕</button>
              </div>
              {reports.length === 0 ? (
                <div className="reports-empty">
                  <p>Noch keine Reports für diesen Job.</p>
                  <p className="reports-empty-sub">Führe den Job jetzt aus oder warte auf den geplanten Zeitpunkt.</p>
                </div>
              ) : (
                <div className="reports-list">
                  {reports.map(r => (
                    <div key={r.id} className="report-item" onClick={() => setViewReport(r.id)}>
                      <div className="report-item-left">
                        <span className="report-item-date">{fmtDate(r.created_at)}</span>
                        <span className="report-item-topic">{r.topic}</span>
                      </div>
                      <div className="report-item-right">
                        <span className="report-item-meta">{r.model} · {r.search_count} Suchen</span>
                        {typeof r.kern_count === 'number' && (
                          <span className={`meta-chip ${r.kern_count >= 2 ? 'meta-chip-accent' : 'meta-chip-muted'}`}
                            title={`Quellen-Qualität: ${r.kern_count} KERN-Quellen (Primärquellen wie DOI/arXiv/EUR-Lex), ${r.kontext_count} KONTEXT — von ${r.source_count} URLs gesamt`}>
                            {r.kern_count} KERN
                          </span>
                        )}
                        <span className="report-item-link">öffnen →</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
